import { type FC, useState, useMemo, useEffect, useCallback } from "react";
import { Namespace, TranslationKey, useLang, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
  ModalBody,
  SearchablePopover,
  TextArea,
  Separator,
  useToast,
} from "@racoon/ui";
import { badge } from "~/shared/helpers";
import { IcMessage, IcMessageNotSent, IcMessageSent } from "@racoon/icons";
import { ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { enUS, ru } from "date-fns/locale";
import { useListContactsQuery } from "~/shared/api/contact/contact.grpc";
import { fromTimestamp, getEnumName, matchesSearch } from "@racoon/lib";
import { useNotifyReceiverJobCompletedMutation } from "~/shared/api/notification/notification.grpc";
import { useUpdateJobMutation } from "~/shared/api/jobs/job.grpc";

interface Props {
  job: ConnectProto.Job;
}

export const WhatsAppReportModal: FC<Props> = ({ job }) => {
  const t = useT();
  const { lang } = useLang();
  const dateFnsLocale = lang === "ru" ? ru : enUS;
  const [open, setOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedRecipientId, setSelectedRecipientId] = useState<bigint | null>(null);
  const [reportText, setReportText] = useState("");
  const { toast } = useToast();
  const [notifyReceiver, { isLoading: isNotifying }] = useNotifyReceiverJobCompletedMutation();
  const [updateJob, { isLoading: isMarking }] = useUpdateJobMutation();

  const { data: contactList } = useListContactsQuery(
    { ownerOrganizationIds: [job.serviceReceiverId] },
    { skip: !open },
  );

  const recipients = useMemo(
    () =>
      (contactList?.contacts ?? []).filter(
        (c) => !c.pickupLocationId || c.pickupLocationId === job.pickupLocationId,
      ),
    [contactList, job.pickupLocationId],
  );

  // Set primary contact as default when contacts load
  const primaryContactId = job.pickupLocation?.primaryContactId ?? null;
  useEffect(() => {
    if (selectedRecipientId === null && recipients.length > 0) {
      const primary = primaryContactId ? recipients.find((c) => c.id === primaryContactId) : null;
      setSelectedRecipientId(primary ? primary.id : recipients[0].id);
    }
  }, [recipients, primaryContactId]);

  const isCompleted = job.status === ConnectProto.JobStatus.COMPLETED;
  const TriggerIcon = isCompleted
    ? job.receiverNotified
      ? IcMessageSent
      : IcMessageNotSent
    : IcMessage;

  const selectedRecipient = recipients.find((r) => r.id === selectedRecipientId);

  const filteredRecipients = recipients.filter(
    (r) => matchesSearch(r.name, searchValue) || matchesSearch(r.position ?? "", searchValue),
  );

  const noData = t(Namespace.Orders, "whatsappReport.noData");
  const formattedDate = useCallback(
    (dateString?: Date | null) => {
      if (!dateString) return noData;
      try {
        return format(new Date(dateString), "dd.MM.yyyy HH:mm", { locale: dateFnsLocale });
      } catch {
        return noData;
      }
    },
    [dateFnsLocale, noData],
  );

  const finishTime = useMemo(
    () => fromTimestamp(job.factEndAt) ?? fromTimestamp(job.planEndAt),
    [job.factEndAt, job.planEndAt],
  );
  const customer = job.serviceReceiver?.name;
  const jobTypeName = getEnumName(ConnectProto.JobTypeSchema, job.type);

  useEffect(() => {
    if (open && job) {
      const cubicMeters = t(Namespace.Settings, "locationSettings.cubicMeters");
      const volumeSuffix =
        job.type === ConnectProto.JobType.ADD_BUNKER
          ? `, ${t(Namespace.Orders, "whatsappReport.reportVolume", { volume: 0, unit: cubicMeters })}`
          : job.containerType
            ? `, ${t(Namespace.Orders, "whatsappReport.reportVolume", { volume: job.containerType.volume, unit: cubicMeters })}`
            : "";

      const generatedText = `${t(Namespace.Orders, "whatsappReport.reportHeader", { id: String(job.id) })}

${t(Namespace.Orders, "whatsappReport.reportStatus", { status: t(Namespace.Enum, getEnumName(ConnectProto.JobStatusSchema, job.status) as TranslationKey) })}
${t(Namespace.Orders, "whatsappReport.reportTime", { time: formattedDate(finishTime) })}
${t(Namespace.Orders, "whatsappReport.reportCustomer", { customer: customer || noData })}
${t(Namespace.Orders, "whatsappReport.reportAddress", { address: job.pickupLocation?.address || job.pickupLocation?.name || noData })}
${t(Namespace.Orders, "whatsappReport.reportVehicle", { vehicle: job.driverOnShift?.truck?.plateNumber || noData })}
${t(Namespace.Orders, "whatsappReport.reportParams")}${volumeSuffix}.
${t(Namespace.Orders, "whatsappReport.reportJobType", { jobType: t(Namespace.Orders, `whatsappReport.jobType.${jobTypeName}` as TranslationKey) })}

${t(Namespace.Orders, "whatsappReport.reportFooter")}`;

      setReportText(generatedText);
    } else if (!open) {
      setReportText("");
    }
  }, [open, job, t, finishTime, customer, jobTypeName, formattedDate, noData]);

  return (
    <div className="contents" onClick={(e) => e.stopPropagation()}>
      <Modal open={open} onOpenChange={setOpen}>
        <ModalTrigger disabled={!isCompleted} className="flex mx-auto">
          <TriggerIcon
            className={`w-6 h-6 text-brand-blue ${isCompleted ? "cursor-pointer hover:opacity-80 transition-opacity" : "opacity-50 cursor-not-allowed"}`}
          />
        </ModalTrigger>

        <ModalContent className="!gap-0" size="lg">
          <ModalHeader>
            <ModalTitle className="text-xl font-bold">
              {t(Namespace.Orders, "whatsappReport.title")}
            </ModalTitle>
          </ModalHeader>

          <ModalBody className="flex flex-col gap-6 px-4">
            {/* Header Info Block */}
            <div className="bg-text-invert rounded-lg border flex items-stretch text-sm overflow-hidden">
              <div className="text-text-secondary font-medium flex flex-col gap-4 p-3">
                <span className="whitespace-nowrap">Z{String(job.id)}</span>
                {job.routeId ? (
                  <span className="whitespace-nowrap">M{String(job.routeId)}</span>
                ) : null}
              </div>
              <Separator orientation="vertical" className="bg-border-primary" />
              <div className="font-semibold text-text-primary flex flex-col gap-4 p-3 flex-1">
                <span>{job.pickupLocation?.name?.toUpperCase()}</span>
                <span>{customer?.toUpperCase()}</span>
              </div>
            </div>

            {/* Status & Recipient */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-900 font-medium w-32">
                  {t(Namespace.Orders, "whatsappReport.status")}
                </span>
                {badge(
                  t(
                    Namespace.Enum,
                    job.receiverNotified ? "RECEIVER_NOTIFIED" : "RECEIVER_NOT_NOTIFIED",
                  ),
                  (job.receiverNotified
                    ? "RECEIVER_NOTIFIED"
                    : "RECEIVER_NOT_NOTIFIED") as TranslationKey<Namespace.Enum>,
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-900 font-medium w-32">
                  {t(Namespace.Orders, "whatsappReport.recipient")}
                </span>

                <SearchablePopover
                  open={popoverOpen}
                  onOpenChange={setPopoverOpen}
                  searchValue={searchValue}
                  onSearchChange={setSearchValue}
                  inputId="recipient-search"
                  placeholder={t(Namespace.Orders, "whatsappReport.searchPlaceholder")}
                  trigger={
                    <Button
                      id="whatsapp-recipient-select"
                      variant="outline"
                      role="combobox"
                      aria-expanded={popoverOpen}
                      className="flex-1 max-w-[18rem] justify-between font-normal"
                      type="button"
                    >
                      {selectedRecipient ? (
                        <div className="flex items-center  justify-between w-full">
                          <span className="truncate">{selectedRecipient.name}</span>
                          {selectedRecipient.position && (
                            <span className="text-xs font-bold text-brand-blue uppercase">
                              {selectedRecipient.position}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 font-normal">
                          {t(Namespace.Orders, "whatsappReport.selectPlaceholder")}
                        </span>
                      )}
                      <ChevronDown className="w-4 h-4 ml-2 text-black flex-shrink-0" />
                    </Button>
                  }
                >
                  <div className="flex flex-col">
                    {filteredRecipients.map((recipient) => (
                      <div
                        key={String(recipient.id)}
                        className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                          setSelectedRecipientId(recipient.id);
                          setPopoverOpen(false);
                          setSearchValue("");
                        }}
                      >
                        <span className="text-sm font-medium">{recipient.name}</span>
                        {recipient.position && (
                          <span className="text-xs font-bold text-brand-blue uppercase">
                            {recipient.position}
                          </span>
                        )}
                      </div>
                    ))}
                    {filteredRecipients.length === 0 && (
                      <div className="px-3 py-4 text-center text-sm text-gray-500">
                        {recipients.length === 0
                          ? t(Namespace.Orders, "whatsappReport.noContacts")
                          : t(Namespace.Orders, "whatsappReport.noResults")}
                      </div>
                    )}
                  </div>
                </SearchablePopover>
              </div>
            </div>

            {/* Report Text */}
            <div className="gap-2 flex flex-col">
              <div className="text-xs uppercase text-text-primary font-bold">
                {t(Namespace.Orders, "whatsappReport.reportTextLabel")}
              </div>

              <div className="border border-gray-200 rounded-lg bg-white">
                <TextArea
                  className="text-sm !bg-white !p-4 h-[16rem]"
                  style={{ width: "100%" }}
                  value={reportText}
                  onChange={(e) => setReportText(e.target.value)}
                  variant="soft"
                  size="3"
                />
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex gap-4 mt-2">
              <Button
                className="flex-1 font-medium bg-blue-100 text-brand-blue hover:bg-blue-200 border-none"
                variant="opacityBlueWithoutOutline"
                disabled={!selectedRecipientId || isNotifying}
                onClick={async () => {
                  if (!selectedRecipientId) return;
                  toast({ title: t(Namespace.Orders, "whatsappReport.templateNotice") });
                  try {
                    await notifyReceiver({
                      jobId: job.id,
                      contactId: selectedRecipientId,
                      serviceProviderId: job.serviceProviderId,
                    }).unwrap();
                    toast({ title: t(Namespace.Orders, "whatsappReport.sentSuccess") });
                  } catch {}
                }}
              >
                {t(Namespace.Orders, "whatsappReport.sendViaRaccoon")}
              </Button>
              <Button
                variant="opacityBlueWithOutline"
                disabled={!selectedRecipientId || isMarking}
                onClick={async () => {
                  if (!selectedRecipient?.phone) return;
                  const phone = selectedRecipient.phone.replace(/[^\d+]/g, "");
                  const url = `https://wa.me/${phone}?text=${encodeURIComponent(reportText)}`;
                  window.open(url, "_blank");
                  try {
                    await updateJob({
                      id: job.id,
                      receiverNotified: true,
                      serviceProviderId: job.serviceProviderId || undefined,
                      serviceReceiverId: job.serviceReceiverId || undefined,
                    }).unwrap();
                    toast({ title: t(Namespace.Orders, "whatsappReport.markedNotified") });
                  } catch {}
                }}
              >
                {t(Namespace.Orders, "whatsappReport.sendViaWhatsapp")}
              </Button>
            </div>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};
